import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  Box,
  Button,
  IconButton,
  Dialog,
  Grid,
  Typography,
  InputAdornment,
  TextField,
  MenuItem,
  Tooltip,
} from '@material-ui/core'
import { compose } from 'redux'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'
import CloseIcon from '@material-ui/icons/Close'
import { Translate } from 'react-localize-redux'
import { InnrHead } from 'containers'
import { DatePicker, Input, TimePicker, SelectedMenu } from 'components'
import Rating from '@material-ui/lab/Rating'
import PlacesSearchField from 'components/googlemaps/PlacesSearchField'
import FilterCenterFocusIcon from '@material-ui/icons/FilterCenterFocus'
import {
  concatDateAndTimeOfTwoDatesWithUserTimezoneAndUtcFormat,
  formatDateTimeToUtcDateTimeWithUserTimezone,
  formatLocalDateTimeToUserTimezoneDateTime,
} from 'utils/formatDate'
import InfoIcon from '@material-ui/icons/Info'
import { trainings } from 'redux/modules'
import LockIconComponent from 'components/UpgradeMembership/LockIconComponent'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import useTeamFeature from 'hooks/useTeamFeature'
import useFeatureAccessGuard from 'hooks/useFeatureAccessGuard'
import { useStyles } from '../styles'
import { DialogContent } from './StyledComponents'
import TEAM_FEATURE_FLAGS from 'constants/teamFeatureFlags'

const CreateTrainingModal = ({
  open,
  handleClose,
  timezone,
  dateTimeFormat,
  teamAvailabilityCutOffTime,
  match: {
    params: { seasonId },
  },
  seasons,
  createTraining,
}) => {
  const classes = useStyles()
  const [intensity, changeIntensity] = useState(0.0)
  const cutoffTimeOptions = [0, 1, 3, 6, 12, 24, 48]
  const [seasonUuid, setSeasonUuid] = useState(null)

  const featureGuard = useFeatureAccessGuard()
  const { enabled: hasCutoffTimeEnabled } = useTeamFeature(
    TEAM_FEATURE_FLAGS.cutoff_time_enabled
  )

  useEffect(() => {
    if (seasons.length > 0) {
      const season = seasons.find((s) => s.id === +seasonId)
      setSeasonUuid(season.uuid)
    }
  }, [seasons])

  const getEventDefaultDateTimeInUserTimezone = () => {
    const nowDate = new Date()

    nowDate.setMinutes(nowDate.getMinutes() + 30)

    const momentDateTimeInUserTimezone =
      formatLocalDateTimeToUserTimezoneDateTime(nowDate, timezone)
    const jsDateTime = new Date(momentDateTimeInUserTimezone)

    return jsDateTime
  }
  const dateAfterToday = new Date()
  dateAfterToday.setDate(dateAfterToday.getDate() + 7)

  const [trainingData, setTrainingData] = useState({
    date: getEventDefaultDateTimeInUserTimezone(),
    location: '',
    street: '',
    city: '',
    country: '',
    postal_code: '',
    lat: '',
    lng: '',
    state: '',
    focus: '',
    intensity: 0,
    end_time: null,
    availability_cutoff_time: null,
    details: '',
  })
  useEffect(() => {
    if (teamAvailabilityCutOffTime >= 0) {
      setTrainingData({
        ...trainingData,
        availability_cutoff_time: teamAvailabilityCutOffTime,
      })
    }
  }, [teamAvailabilityCutOffTime])

  const handleDateChange = (date) => {
    setTrainingData({ ...trainingData, date })
  }

  const handleTextBox = (event) => {
    const {
      target: { name, value },
    } = { ...event }
    setTrainingData({
      ...trainingData,
      [name]: value,
    })
  }
  const onChangeRating = (newRating) => {
    changeIntensity(newRating)
    setTrainingData({ ...trainingData, intensity: newRating })
  }
  const handleCutoffTimeChange = (event) => {
    if (featureGuard(hasCutoffTimeEnabled)) return

    const { value } = event.target
    setTrainingData({ ...trainingData, availability_cutoff_time: value })
  }
  const handleEndTimeChange = (time) => {
    setTrainingData({ ...trainingData, end_time: time })
  }

  const newTraining = () => {
    const {
      date,
      location,
      focus: trainingFocus,
      street,
      city,
      country,
      postal_code: postalCode,
      lat,
      lng,
      state,
      details,
      end_time: endTime,
      availability_cutoff_time: availabilityCutoffTime,
    } = { ...trainingData }

    createTraining(seasonUuid, {
      start_datetime: formatDateTimeToUtcDateTimeWithUserTimezone(
        date,
        timezone
      ),
      location,
      focus: trainingFocus,
      street,
      city,
      country,
      postal_code: postalCode,
      lat: lat.toString(),
      lng: lng.toString(),
      state,
      intensity: intensity ? intensity.toFixed(2) : 0.0,
      details: details || '',
      end_datetime: endTime
        ? concatDateAndTimeOfTwoDatesWithUserTimezoneAndUtcFormat(
            date,
            endTime,
            timezone
          )
        : null,
      availability_cutoff_time: availabilityCutoffTime,
    }).then((res) => {
      handleClose(res.response.data)
    })
  }

  return (
    <Dialog
      onClose={() => handleClose()}
      aria-labelledby="customized-dialog-title"
      open={open}
      maxWidth="sm"
      fullWidth
      className={classes.createTrainingWrapper}
      PaperProps={{ elevation: 0 }}
    >
      <InnrHead title={<Translate id="button.create-plan-training" />}>
        <IconButton onClick={() => handleClose()} size="small">
          <CloseIcon />
        </IconButton>
      </InnrHead>
      <DialogContent dividers>
        <Grid item xs={12} md={12}>
          <DatePicker
            required
            label={<Translate id="training.date" />}
            selectedValue={trainingData.date}
            onDateChange={handleDateChange}
            isEditMode={false}
            dateFormat={dateTimeFormat}
          />
        </Grid>
        <Grid item xs={12}>
          <TimePicker
            selectedValue={trainingData.date}
            onDateChange={handleDateChange}
            label={<Translate id="training.time" />}
            isEditMode={false}
          />
        </Grid>

        <Grid item xs={12}>
          <PlacesSearchField
            error={trainingData.error_location}
            helperText={
              trainingData.error_location ? (
                <Translate id="events.validation.location" />
              ) : (
                ''
              )
            }
            defaultValue={trainingData.location}
            savedValue={trainingData.location}
            onChange={handleTextBox}
            changeFormData={setTrainingData}
            eventData={trainingData}
          />
        </Grid>
        <Grid item xs={12}>
          <Input
            label={<Translate id="form.training-focus" />}
            name="focus"
            value={trainingData.focus}
            onChange={handleTextBox}
            margin="dense"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <FilterCenterFocusIcon />
                </InputAdornment>
              ),
            }}
          />
        </Grid>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Typography component="legend" style={{ color: 'gray' }}>
              <Translate id="training.intensity" />
            </Typography>
            <Rating
              name="training-rating"
              value={trainingData.intensity}
              precision={0.5}
              onChange={(e, v) => onChangeRating(v)}
            />
            {intensity > 0 && (
              <Button
                color="secondary"
                size="small"
                variant="outlined"
                style={{ float: 'right' }}
                onClick={() => onChangeRating(0.0)}
              >
                Reset <Translate id="training.intensity" />
              </Button>
            )}
          </Grid>
          <Grid item xs={6}>
            <TimePicker
              selectedValue={trainingData.end_time}
              onDateChange={handleEndTimeChange}
              label={<Translate id="matches.end-time" />}
              isEditMode={!!trainingData.end_time}
            />
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <SelectedMenu
            id="cutoff-time"
            value={trainingData.availability_cutoff_time}
            margin="dense"
            className={classes.select}
            onChange={handleCutoffTimeChange}
            label={<Translate id="player.event-response.cutoff-time" />}
            endAdornment={
              <InputAdornment position="start" className={classes.infoIcon}>
                <Tooltip
                  title={<Translate id="player.event-response.info-message" />}
                  placement="bottom-end"
                >
                  <InfoIcon />
                </Tooltip>
              </InputAdornment>
            }
            IconComponent={
              !hasCutoffTimeEnabled
                ? (props) => (
                    <LockIconComponent
                      context="select"
                      style={{ transform: 'none', transition: 'none' }} // Prevent lock icon rotation
                      {...props}
                    />
                  )
                : ArrowDropDownIcon
            }
          >
            {cutoffTimeOptions.map((option) => (
              <MenuItem key={option} value={option}>
                {option === 0 ? (
                  <Translate id="player.event-response.disabled" />
                ) : (
                  <>
                    {option} <Translate id="player.event-response.hours" />
                  </>
                )}{' '}
              </MenuItem>
            ))}
          </SelectedMenu>
        </Grid>
        <Grid item xs={12}>
          <TextField
            name="details"
            variant="outlined"
            margin="dense"
            defaultValue={trainingData.details}
            value={trainingData.details}
            multiline
            minRows={4}
            label={<Translate id="matches.additional-info" />}
            onChange={handleTextBox}
            className={classes.textarea}
          />
        </Grid>
        <Grid item xs={12}>
          <Box mt={4}>
            <Button
              onClick={() => newTraining(trainingData)}
              variant="contained"
              fullWidth
              size="large"
              color="primary"
            >
              <Translate id="button.create-plan-training" />
            </Button>
          </Box>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}

CreateTrainingModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  teamAvailabilityCutOffTime: PropTypes.number.isRequired,
  timezone: PropTypes.string.isRequired,
  dateTimeFormat: PropTypes.string.isRequired,
  match: PropTypes.shape({
    params: PropTypes.shape({
      seasonId: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  seasons: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      uuid: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
  createTraining: PropTypes.func.isRequired,
}

export default compose(
  connect(
    ({ auth, team, seasons: { items } }) => ({
      timezone: auth.user.tz || auth.user.time_zone,
      dateTimeFormat: auth.user.datetime_format,
      teamAvailabilityCutOffTime: team.availability_cutoff_time,
      teamId: team.id,
      seasons: items,
    }),
    {
      createTraining: trainings.createTraining,
    }
  ),
  withRouter
)(CreateTrainingModal)
